/** Copyright (c) 2021 T. Rowe Price. All rights reserved. */

import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import map from 'lodash/map';
import moment from 'moment';
import { createSelector } from 'reselect';

import { today } from 'services/Dates';
import { getView } from 'state/view/ViewSelectors';
import { minInceptionDate } from 'services/Calculation';
import {
  ACTIVE_PLUS,
  ADVICE_MANAGED_CLASS,
  MUTUAL_FUND,
  BROKERAGE,
  COLLEGE_SAVINGS_CLASS,
  NON_RETIREMENT_CLASS,
  OWNED_CLASS,
  productMapByNumber,
  RETIREMENT_CLASS,
  VIEW_ONLY_CLASS,
  isRetirementAccount,
  isOwnedAccount,
  isViewOnlyAccount
} from 'services/Products';
import {
  ALL_LABEL,
  APP_LABEL,
  BROKERAGE_LABEL,
  COLLEGE_SAVINGS_LABEL,
  AK_ONLY_COLLEGE_SAVINGS_LABEL,
  MUTUAL_FUND_LABEL,
  NON_RETIREMENT_LABEL,
  OWNED_LABEL,
  RAS_LABEL,
  RETIREMENT_LABEL,
  VIEW_ONLY_LABEL
} from 'services/Filters';
import { getAccounts, getAccountsById } from 'state/account/AccountSelectors';
import { getPortfolioGrowthFilter } from './PortfolioGrowthFilterSelectors';
import { getFilterForFilterType } from './PerformancePageSelectors';

export const getAccountPerformanceTable = (state) =>
  state.accountPerformanceTable;
export const getAccountPerformanceMap = (state) => state.accountPerformanceMap;
export const getPerformancePeriods = (state) => state.performancePeriods;
const getTotalPerformance = (state) => state.totalPerformance;
export const beginDate = (state) => (state.view ? state.view.beginDate : '');
export const getPeriods = (state) => state.periods;
export const getSelections = (state) => state.selections;

// const SINCE_INCEPTION = 'sinceInception';
/**
 * Return the list of accounts for the performance page filtered by both the active hide/show
 * pre-filter and the account type filter selections. This function is not intended to be used
 * anywhere else except for the display components on the performance page.
 *
 * @param accounts
 * @param performanceFilter
 * @param selections
 * @returns {*[]|*}
 */
export const applyFiltersForPerformance = (
  accounts,
  performanceFilter,
  selections
) => {
  if (isEmpty(accounts)) {
    return [];
  }
  // check for accounts that are not hidden (subset checks boolean value)
  const { subset } = selections.accountsPreFilter;
  const subsetAccounts = accounts.filter((a) => subset[a.accountId]);
  // check for the selected account type (accountIds checks for object)
  const { accountIds } = performanceFilter;

  return subsetAccounts.filter((a) => accountIds[a.accountId] != null);
};

export const getPerformanceInceptionReturn = createSelector(
  getTotalPerformance,
  (totalPerformance) =>
    totalPerformance && totalPerformance.inceptionReturn
      ? totalPerformance.inceptionReturn
      : 0
);

export const getPerformanceTableData = createSelector(
  getAccountPerformanceTable,
  (performanceTable) => {
    return (
      performanceTable &&
      performanceTable.map((tableRow) => {
        const { inceptionDate } = tableRow;

        const yearsSinceInception = moment().diff(inceptionDate, 'years');
        let modifiedTableRow = tableRow;
        if (isNumber(yearsSinceInception)) {
          switch (true) {
            case yearsSinceInception < 1:
              modifiedTableRow = {
                ...tableRow,
                oneYearReturn: null,
                threeYearReturn: null,
                fiveYearReturn: null,
                tenYearReturn: null
              };
              break;
            case yearsSinceInception < 3:
              modifiedTableRow = {
                ...tableRow,
                threeYearReturn: null,
                fiveYearReturn: null,
                tenYearReturn: null
              };
              break;
            case yearsSinceInception < 5:
              modifiedTableRow = {
                ...tableRow,
                fiveYearReturn: null,
                tenYearReturn: null
              };
              break;
            case yearsSinceInception < 10:
              modifiedTableRow = {
                ...tableRow,
                tenYearReturn: null
              };
              break;
            default:
          }
        }
        return modifiedTableRow;
      })
    );
  }
);

export const getFilteredSelection = createSelector(
  getFilterForFilterType,
  (performanceFilter) => {
    const { filter } = performanceFilter;

    switch (filter) {
      case RAS_LABEL: {
        return ADVICE_MANAGED_CLASS;
      }
      case APP_LABEL: {
        return ACTIVE_PLUS;
      }
      case MUTUAL_FUND_LABEL: {
        return MUTUAL_FUND;
      }
      case BROKERAGE_LABEL: {
        return BROKERAGE;
      }
      case COLLEGE_SAVINGS_LABEL: {
        return COLLEGE_SAVINGS_CLASS;
      }
      case AK_ONLY_COLLEGE_SAVINGS_LABEL: {
        return COLLEGE_SAVINGS_CLASS;
      }
      case RETIREMENT_LABEL: {
        return RETIREMENT_CLASS;
      }
      case NON_RETIREMENT_LABEL: {
        return NON_RETIREMENT_CLASS;
      }
      case OWNED_LABEL: {
        return OWNED_CLASS;
      }
      case VIEW_ONLY_LABEL: {
        return VIEW_ONLY_CLASS;
      }
      case ALL_LABEL:
      default:
        return 0;
    }
  }
);

// TODO: This can be refactored in US398725
export const getOldestInceptionDateBySelection = createSelector(
  getAccountsById,
  getAccountPerformanceMap,
  getView,
  getFilteredSelection,
  getFilterForFilterType,
  (
    accountsById,
    accountPerformanceMap,
    view,
    filteredSelection,
    performanceFilter
  ) => {
    const defaultInceptionDate = view['oldestInceptionDate']
      ? view['oldestInceptionDate']
      : today;
    const { accountIds } = performanceFilter;
    const selectedAccountIds = Object.keys(accountIds);

    if (accountPerformanceMap) {
      const flattenedAccounts = map(accountPerformanceMap, (a) => {
        const { retirementAccount, isViewOnly, owner } =
          accountsById[a.accountId];
        return {
          retirementAccount,
          isViewOnly,
          owner,
          ...a
        };
      });

      if (flattenedAccounts) {
        // All filter selection
        if (filteredSelection === 0) {
          const selectedAccounts = flattenedAccounts.filter((a) =>
            selectedAccountIds.includes(a.accountId)
          );
          const inceptionDateForSelection = getInceptionDate(selectedAccounts);
          return inceptionDateForSelection
            ? inceptionDateForSelection
            : defaultInceptionDate;
        }

        // 529 College Savings filter
        if (filteredSelection === COLLEGE_SAVINGS_CLASS) {
          const filteredAccounts = getFilteredAccountsForSelection(
            COLLEGE_SAVINGS_CLASS,
            flattenedAccounts,
            selectedAccountIds
          );
          const inceptionDateForSelection = getInceptionDate(filteredAccounts);
          return inceptionDateForSelection
            ? inceptionDateForSelection
            : defaultInceptionDate;
        }

        //RAS Filter
        if (filteredSelection === ADVICE_MANAGED_CLASS) {
          const filteredAccounts = getFilteredAccountsForSelection(
            ADVICE_MANAGED_CLASS,
            flattenedAccounts,
            selectedAccountIds
          );
          const inceptionDateForSelection = getInceptionDate(filteredAccounts);
          return inceptionDateForSelection
            ? inceptionDateForSelection
            : defaultInceptionDate;
        }

        // Retirement filter selection
        if (filteredSelection === RETIREMENT_CLASS) {
          const filteredAccounts = flattenedAccounts.filter(
            (a) =>
              isRetirementAccount(a) && selectedAccountIds.includes(a.accountId)
          );
          const inceptionDateForSelection = getInceptionDate(filteredAccounts);
          return inceptionDateForSelection
            ? inceptionDateForSelection
            : defaultInceptionDate;
        }

        // Non-Retirement filter selection
        if (filteredSelection === NON_RETIREMENT_CLASS) {
          const filteredAccounts = flattenedAccounts.filter(
            (a) =>
              !isRetirementAccount(a) &&
              selectedAccountIds.includes(a.accountId)
          );
          const inceptionDateForSelection = getInceptionDate(filteredAccounts);
          return inceptionDateForSelection
            ? inceptionDateForSelection
            : defaultInceptionDate;
        }

        // Owned filter selection
        if (filteredSelection === OWNED_CLASS) {
          const filteredAccounts = flattenedAccounts.filter(
            (a) => isOwnedAccount(a) && selectedAccountIds.includes(a.accountId)
          );
          const inceptionDateForSelection = getInceptionDate(filteredAccounts);
          return inceptionDateForSelection
            ? inceptionDateForSelection
            : defaultInceptionDate;
        }

        // View Only filter selection
        if (filteredSelection === VIEW_ONLY_CLASS) {
          const filteredAccounts = flattenedAccounts.filter(
            (a) =>
              isViewOnlyAccount(a) && selectedAccountIds.includes(a.accountId)
          );
          const inceptionDateForSelection = getInceptionDate(filteredAccounts);
          return inceptionDateForSelection
            ? inceptionDateForSelection
            : defaultInceptionDate;
        }

        // all other product types
        const productName = productMapByNumber[filteredSelection];
        if (productName) {
          const accounts = getFilteredAccountsForSelection(
            productName,
            flattenedAccounts,
            selectedAccountIds
          );
          const inceptionDateForSelection = getInceptionDate(accounts);
          return inceptionDateForSelection
            ? inceptionDateForSelection
            : defaultInceptionDate;
        }
      }
    }

    return defaultInceptionDate;
  }
);

const getFilteredAccountsForSelection = (
  productName,
  accounts,
  selectedAccountIds
) => {
  return accounts.filter(
    (a) =>
      a.productClass === productName && selectedAccountIds.includes(a.accountId)
  );
};

const getInceptionDate = (accounts) => {
  const inceptionDateForSelection = minInceptionDate(accounts);
  return inceptionDateForSelection ? inceptionDateForSelection : null;
};

export const getAccountsFilteredForPortfolioGrowth = createSelector(
  getAccounts,
  getPortfolioGrowthFilter,
  getSelections,
  (accounts, filter, selections) =>
    applyFiltersForPerformance(accounts, filter, selections)
);
